// lib/freeze-watchdog.ts
//
// Detects whether THIS page's JS thread has been suspended/throttled for
// longer than expected — the iOS WebKit freeze investigation (PROJECT.md,
// "Found (not yet fixed)") has so far relied entirely on the page's own clog
// buffer as evidence, which is exactly what stops updating if the thread is
// actually dead. A frozen page can't self-report; the best a frozen page CAN
// do is leave a trail the moment it un-freezes, by comparing how much real
// time passed against how much it expected to pass.
//
// Mechanism: a setInterval tick at HEARTBEAT_INTERVAL_MS, measured with
// performance.now() (monotonic — unaffected by wall-clock/timezone changes,
// unlike Date.now()). If the actual gap between ticks is far longer than the
// interval, the thread was suspended for that excess — by the browser
// throttling background timers, OS-level tab suspension, or (the thing we're
// trying to catch) a genuine freeze. A clean foreground/background/foreground
// cycle is NOT itself an anomaly — timers are *expected* to stop firing while
// hidden and catch up (once) on resume; logging every backgrounding would be
// noise. What's interesting is drift while still reportedly visible, or an
// implausibly large gap on resume that suggests more than ordinary suspension.

import { clog } from "./client-log";

const HEARTBEAT_INTERVAL_MS = 15_000;
// Any tick arriving more than this many ms late is logged. Generous slack
// above the interval itself to avoid flagging ordinary GC pauses / event-loop
// contention — this is hunting for suspensions measured in tens of seconds to
// hours, not normal jitter.
const DRIFT_THRESHOLD_MS = 45_000;

let _installed = false;
let _lastTickPerf = 0;
let _lastTickWasHidden = false;

function tick(): void {
  const now = performance.now();
  const expectedGap = HEARTBEAT_INTERVAL_MS;
  const actualGap = now - _lastTickPerf;
  const driftMs = actualGap - expectedGap;
  const hidden = document.visibilityState === "hidden";

  if (driftMs > DRIFT_THRESHOLD_MS) {
    // This tick can fire from a visibilitychange resume, at which point
    // `hidden` is already false (the transition already happened) even
    // though the entire gap was an ordinary background suspension. The
    // anomalous case isn't "is it hidden right now" — it's "was it hidden
    // for the gap we're measuring," which `_lastTickWasHidden` alone
    // already tells us. Only flag WARN when the page claims it was visible
    // the *whole* time (never reported hidden), since that's the case
    // where something suspended JS without ever signaling backgrounding —
    // the actual freeze signature this module exists to catch.
    const level = _lastTickWasHidden ? "info" : "warn";
    clog[level]("freeze-watchdog", "heartbeat drift detected", {
      driftMs: Math.round(driftMs),
      actualGapMs: Math.round(actualGap),
      expectedGapMs: expectedGap,
      visibilityNow: document.visibilityState,
      wasHiddenLastTick: _lastTickWasHidden,
    });
  }

  _lastTickPerf = now;
  _lastTickWasHidden = hidden;
}

// Also fires immediately on any visibilitychange so a freeze that happens to
// end exactly when the user switches tabs/foregrounds isn't only caught on
// the next periodic tick (which could itself be up to HEARTBEAT_INTERVAL_MS
// further delayed).
function onVisibilityChange(): void {
  tick();
}

export function installFreezeWatchdog(): void {
  if (_installed || typeof window === "undefined") return;
  _installed = true;
  _lastTickPerf = performance.now();
  _lastTickWasHidden = document.visibilityState === "hidden";
  setInterval(tick, HEARTBEAT_INTERVAL_MS);
  document.addEventListener("visibilitychange", onVisibilityChange);
}
